/**
 * Slider arrows component.
 */
import ArrowLeftIcon from '@components/atoms/icons/arrowLeft';
import ArrowRightIcon from '@components/atoms/icons/arrowRight';
import Colors from '@shared/assets/style/default/color';
import Focus from '@shared/types/enums/focus';
import SliderArrowsContainer from '@components/atoms/container/sliderArrowsContainer';
import SliderLeftArrowContainer from '@components/atoms/container/sliderLeftArrowContainer';
import SliderRightArrowContainer from '@components/atoms/container/sliderRightArrowContainer';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { ArrowDirection } from '@shared/types/enums/direction';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useState } from 'react';

/**
 * Constants
 */
const ICON_HEIGHT = 40; // Icon height
const ICON_WIDTH = 25; // Icon width
const OPACITY_DELAY = 200; // Opacity delay
const PRESS_DELAY = 100; // Press delay

/**
 * Props for the SliderArrows component.
 * @param onClick - The function to call when the arrows are clicked.
 */
export interface SliderArrowsProps {
  onClick: (direction: ArrowDirection) => void;
}

/**
 * Renders the left and right arrows when the sliders area has focus (Focus.SLIDERS).
 * @returns {JSX.Element} The SliderArrows component.
 */
export default function SliderArrows({ onClick }: SliderArrowsProps) {
  const focus = useAppSelector(selectFocus);
  const [leftPressed, setLeftPressed] = useState(false);
  const [rightPressed, setRightPressed] = useState(false);

  useKeyboard({
    focus: Focus.SLIDERS,
    keyDownHandler: (key) => {
      switch (key) {
        case 'LEFT':
          setLeftPressed(true);
      }
      switch (key) {
        case 'RIGHT':
          setRightPressed(true);
          break;
      }
    },
    keyUpHandler: (key) => {
      if (key === 'LEFT') {
        setTimeout(() => {
          setLeftPressed(false);
        }, PRESS_DELAY);
      }
      if (key === 'RIGHT') {
        setTimeout(() => {
          setRightPressed(false);
        }, PRESS_DELAY);
      }
    },
  });

  return focus === Focus.SLIDERS ? (
    <SliderArrowsContainer delay={OPACITY_DELAY}>
      {/* Left arrow */}
      <SliderLeftArrowContainer onClick={() => onClick(ArrowDirection.LEFT)}>
        <ArrowLeftIcon
          width={ICON_WIDTH}
          height={ICON_HEIGHT}
          color={leftPressed ? Colors['selector'] : '#FFF'}
        />
      </SliderLeftArrowContainer>
      {/* Right arrow */}
      <SliderRightArrowContainer onClick={() => onClick(ArrowDirection.RIGHT)}>
        <ArrowRightIcon
          width={ICON_WIDTH}
          height={ICON_HEIGHT}
          color={rightPressed ? Colors['selector'] : '#FFF'}
        />
      </SliderRightArrowContainer>
    </SliderArrowsContainer>
  ) : null;
}
